<!--
    https://vue-multiselect.js.org/
-->
<template>
  <multiselect
    :id="mainId"
    test-id="al-select"
    v-bind="$props"
    :close-on-select="closeOnSelect"
    tag-placeholder="Add this as new tag"
    :searchable="searchable || multiple"
    :allow-empty="allowEmpty || multiple"
    :taggable="(multiple && allowEmpty && searchable && !closeOnSelect) || taggable"
    class="al-multiselect"
    :style="`width:${width}`"
    :class="[
      size,
      { 'multiselect__checkbox-options': checkboxOption },
      {
        multiselect__searchable: checkboxOption || searchable || multiple
      }
    ]"
    :loading="isLoading"
    @search-change="onSearchChange"
    @input="onInput"
    @select="$emit('select', $event)"
    @remove="$emit('remove', $event)"
    @tag="$emit('tag', $event)"
    @close="close"
    @open="open"
  >
    <al-icon
      v-show="!isLoading"
      slot="caret"
      slot-scope="{ toggle }"
      class="caret"
      :icon="isOpen ? 'chevron-up' : 'chevron-down'"
      @mousedown.prevent.stop="toggle"
    />

    <template slot="tag" slot-scope="scopeProps">
      <div test-id="tag" class="plvx-tag">
        {{ scopeProps.option.name }}
        <al-icon icon="cross" @click="scopeProps.remove(scopeProps.option)" />
      </div>
    </template>

    <template slot="option" slot-scope="scopeProps">
      <!-- TODO: try to put this ourside the AlSelect-->
      <div v-if="checkboxOption" class="checkbox-option">
        <div test-id="option" class="al-select-option">
          <al-checkbox v-model="scopeProps.option.isSelected" disabled single></al-checkbox>
          <span class="option__title">{{ scopeProps.option.name }}</span>
        </div>
      </div>

      <div v-else slot="option">
        <div test-id="option" class="al-select-option">
          {{ scopeProps.option.name || scopeProps.search }}
          <al-icon class="check" icon="check" />
        </div>
      </div>
    </template>
  </multiselect>
</template>

<script>
import Multiselect from 'vue-multiselect';
import AlIcon from '../../inline/icon/AlIcon.vue';
import AlCheckbox from '../checkbox/AlCheckbox.vue';

export default {
  components: {
    Multiselect,
    AlIcon,
    AlCheckbox
  },
  extends: Multiselect,
  model: {
    prop: 'value',
    event: 'input'
  },
  props: {
    ...Multiselect.props,
    id: {
      type: String,
      default: null
    },
    closeOnSelect: {
      type: Boolean,
      required: false,
      default: true
    },
    size: {
      type: String,
      default: ''
    },
    trackBy: {
      type: String,
      default: 'id'
    },
    label: {
      type: String,
      default: 'name'
    },
    width: {
      type: String,
      default: 'auto'
    },

    internalSearch: {
      type: Boolean,
      default: true
    },
    isLoading: Boolean,
    searchable: Boolean,
    allowEmpty: Boolean,
    hideSelected: Boolean,
    clearOnSelect: Boolean,
    preselectFirst: Boolean,
    showLabels: Boolean,
    multiple: Boolean,
    checkboxOption: Boolean
  },
  data() {
    return {
      isOpen: false
    };
  },
  computed: {
    mainId() {
      // eslint-disable-next-line no-underscore-dangle
      const id = this.id ? this.id : this._uid;
      return `al-select-${id}`;
    }
  },
  methods: {
    onInput(value) {
      this.$emit('input', value);
    },
    onSearchChange(query) {
      this.$emit('search-change', query);
    },
    close(event) {
      this.isOpen = false;
      this.$emit('close', event);
    },
    open(event) {
      this.isOpen = true;
      this.$emit('open', event);
    }
  }
};
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>

<style lang="scss">
$border-input-radius: 4px;

$input-height: 44px;

$input-sm-height: 36px;
$font-size: 16px;

$space-menu: 5px;

.al-multiselect {
  height: $input-height;
  min-height: $input-height;
  min-width: 125px;

  .caret {
    background: color(white);
    border: 1px solid color(white, darken, 2);
    border-bottom-right-radius: $border-input-radius;
    border-top-right-radius: $border-input-radius;
    box-sizing: border-box;
    height: $input-height;
    position: absolute;
    right: 0;
    top: 0;
    width: $input-height;
    z-index: 2;
  }

  .multiselect__content-wrapper {
    background-color: white;
    border: 1px solid color(white, darken, 2);
    border-radius: 5px;
    box-shadow: 0 8px 4px -4px rgba(54, 64, 63, 0.1);
    padding-bottom: 5px;
    padding-top: 5px;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  &.multiselect__searchable {
    .multiselect__content-wrapper {
      border-radius: 0 0 5px 5px;
      border-top: 0;
      padding-top: 0;
    }
  }

  .multiselect {
    &__single {
      background-color: transparent;
      font-size: $font-size;
      left: 0;
      padding: 12px 16px;
      position: absolute;
      top: 0;
    }

    &__placeholder {
      font-size: $font-size;
      height: 100%;
      left: 0;
      margin-bottom: 0;
      padding: 0;
      padding: 10px 16px;
      position: absolute;
      top: 0;
    }

    &__content {
      width: 100%;
    }

    &__spinner {
      background: transparent;
      height: $input-height;
    }

    &__tags {
      border: 0;
      min-height: $input-height;
      padding: 0;
      padding: 12px;

      &-wrap {
        align-items: center;
        border: 1px solid color(white, darken, 2);
        border-radius: 5px;
        display: inline-flex !important;
        height: $input-height;
        justify-content: flex-start;
        left: 0;
        max-width: 100%;
        min-width: 100%;
        overflow-x: auto;
        padding: 0 5px;
        padding-right: 45px;
        position: absolute;
        top: 0;
        width: max-content;

        &::-webkit-scrollbar {
          display: none;
        }

        .plvx-tag {
          align-items: center;

          background: color(white, darken, 1);
          border-radius: 4px;

          color: color(black, lighten, 1);
          display: inline;
          display: flex;
          font-size: 16px;
          justify-content: center;
          margin-right: 5px;
          max-height: 28px;
          min-width: max-content;
          overflow: hidden;
          padding: 5px 10px;
          position: relative;

          .al-icon {
            background: color(green, lighten, 3);
            color: color(green, darken, 1);
            cursor: pointer;
            height: 100%;
            opacity: 0;
            position: absolute;
            right: 0;
            top: 0;
            width: 28px;

            &:hover {
              background: color(green, lighten, 3);
              box-shadow: -3px -1px 4px -2px rgba(0, 0, 0, 0.35);
              opacity: 1;
            }
          }

          &:hover {
            background: color(green, lighten, 3);
            color: color(forest-green);
            cursor: pointer;
          }
        }
      }

      .multiselect__input {
        background: color(white);
        border-radius: 5px;
        font-size: $font-size;
        height: $input-sm-height;
        margin-bottom: 0;
        padding-left: 12px;
      }
    }
  }

  &.multiselect--active {
    .caret {
      background: color(green, lighten, 3);
      border-color: color(green);
      color: color(green);
    }

    .multiselect__tags {
      border: 1px solid color(white, darken, 2);
      border-bottom: 0;
      padding-top: 30px;

      &-wrap {
        border-color: color(green);
        box-shadow: 0 8px 4px -4px rgba(54, 64, 63, 0.1);
      }
    }

    &.multiselect__searchable {
      .multiselect__tags {
        padding-top: $input-height + 12px;
      }

      &.multiselect--above {
        .multiselect__tags {
          border-color: transparent;
          padding: 0;
          padding-top: 0 !important;

          .multiselect__input {
            background: white;
            box-sizing: border-box;
            height: $input-height - 2;
            margin: 0;
            width: 100%;
          }

          &::before {
            display: none;
          }
        }

        .multiselect__content-wrapper {
          border: 1px solid #d9e3e2;
          border-radius: 5px;
          box-shadow: none;
          margin-bottom: 5px;
          padding-bottom: 0;
          padding-top: 5px;
        }
      }
    }
  }

  .multiselect__option {
    background-color: #fff;
    color: #68706f;
    position: relative;

    .check {
      color: color(green);
      font-size: 25px;
      line-height: 0.8;
      position: absolute;
      right: 5px;
      visibility: hidden;
    }

    &--highlight {
      background-color: color(green, lighten, 4);
      color: #051f15;
      font-weight: normal;
    }

    &--selected {
      background-color: #f5f8f7;
      color: #9b9f9f;
      font-weight: normal;

      .check {
        color: color(green);
        visibility: visible;
      }
    }
  }

  &.sm {
    height: $input-sm-height;
    min-height: $input-sm-height;

    .caret {
      border: 0;
      height: $input-sm-height;
      width: $input-sm-height;
    }

    .multiselect {
      &__single {
        padding: 8px 12px;
      }

      &__placeholder {
        padding: 6px 12px;
      }

      &__spinner {
        height: $input-sm-height;
      }

      &__tags {
        min-height: $input-sm-height;

        &-wrap {
          background: color(white);
          border: 0;
          height: $input-sm-height;
        }
      }

      &__searchable {
        .multiselect__content-wrapper {
          top: ($input-sm-height * 2) + 28px;
        }
      }
    }

    &.multiselect--active {
      .caret {
        background-color: color(green, lighten, 4);
        color: color(black, lighten, 1);
      }

      .multiselect__tags {
        border: 0;

        &::before {
          border: 1px solid color(white, darken, 2);
          border-bottom: 0;
          border-bottom-left-radius: 0;
          border-bottom-right-radius: 0;
          border-top-left-radius: 5px;
          border-top-right-radius: 5px;
          content: '';
          display: none;
          height: $input-sm-height + 24px + $space-menu;
          left: 0;
          position: absolute;
          top: $input-sm-height + $space-menu;
          width: 100%;
        }

        &-wrap {
          background-color: color(green, lighten, 4);
          box-shadow: none;

          .plvx-tag {
            background: color(green, lighten, 3);
          }
        }
      }

      &.multiselect__searchable {
        .multiselect__tags {
          padding-top: $input-sm-height + 12px + $space-menu;

          &::before {
            display: block;
          }
        }

        &.multiselect--above {
          .multiselect__tags {
            .multiselect__input {
              background-color: color(green, lighten, 4);
              color: color(black, lighten, 1);
              height: $input-sm-height;
            }

            &::before {
              display: none;
            }
          }
        }
      }
    }
  }
}

.multiselect__checkbox-options {
  .checkbox-option {
    align-items: center;
    display: flex;
    flex-direction: row;

    .al-checkbox .btn,
    .al-checkbox .btn-secondary:not(:disabled):not(.disabled).active,
    .al-checkbox .btn-secondary:not(:disabled):not(.disabled):active {
      background: transparent;
    }
  }

  .multiselect__option--selected.multiselect__option--highlight,
  .multiselect__option--highlight {
    background: #f3f3f3;
    color: #444;
  }
}

.al-select-option {
  background: none;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 90%;
}
</style>
